import { PagePaths } from '@revelio/core';

import type { Page } from '../components/PageConfigProvider/types';
import { companyOverview } from '../templates/company-overview-v2';
import { companyScreenerV2 } from '../templates/company-screener-v2';
import { companySentiment } from '../templates/company-sentiment';
import { companyComposition } from '../templates/companyComposition';
import { companyCompositionV2 } from '../templates/companyCompositionV2';
import { companyPay } from '../templates/companyPay';
import { companyPostings } from '../templates/companyPostings';
import { companyTransitions } from '../templates/companyTransitions';
import { companyTransitionsV2 } from '../templates/companyTransitionsV2';
import { geographyScreenerV2 } from '../templates/geography-screener-v2';
import { geographySentiment } from '../templates/geography-sentiment';
import { geographyComposition } from '../templates/geographyComposition';
import { geographyCompositionV2 } from '../templates/geographyCompositionV2';
import { geographyOverview } from '../templates/geographyOverview';
import { geographyPay } from '../templates/geographyPay';
import { geographyPostings } from '../templates/geographyPostings';
import { geographyTransitions } from '../templates/geographyTransitions';
import { geographyTransitionsV2 } from '../templates/geographyTransitionsV2';
import { postingDiscovery } from '../templates/posting-discovery';
import { getAllReportTemplates } from '../templates/report-templates-registry';
import { roleScreenerV2 } from '../templates/role-screener-v2';
import { roleSentiment } from '../templates/role-sentiment';
import { roleComposition } from '../templates/roleComposition';
import { roleCompositionV2 } from '../templates/roleCompositionV2';
import { roleOverview } from '../templates/roleOverview';
import { rolePay } from '../templates/rolePay';
import { rolePostings } from '../templates/rolePostings';
import { roleTransitions } from '../templates/roleTransitions';
import { roleTransitionsV2 } from '../templates/roleTransitionsV2';
import { talentDiscovery } from '../templates/talent-discovery';

const baseTemplates: Page[] = [
  companyOverview,
  geographyOverview,
  roleOverview,

  companyComposition,
  geographyComposition,
  roleComposition,

  companyCompositionV2,
  geographyCompositionV2,
  roleCompositionV2,

  companyTransitions,
  geographyTransitions,
  roleTransitions,

  companyTransitionsV2,
  geographyTransitionsV2,
  roleTransitionsV2,

  companyPostings,
  geographyPostings,
  rolePostings,

  companyPay,
  geographyPay,
  rolePay,

  companySentiment,
  geographySentiment,
  roleSentiment,

  companyScreenerV2,
  geographyScreenerV2,
  roleScreenerV2,

  talentDiscovery,
  postingDiscovery,
];

export const getLocalTemplates = (): Page[] => [
  ...baseTemplates,
  ...getAllReportTemplates(),
];

export const getLocalTemplate = (pageId: string): Page | undefined => {
  const localTemplate = getLocalTemplates().find((t) => t.id === pageId);
  return localTemplate;
};

const PathToPageConfigLookup = {
  [PagePaths.COMPANY_COMPOSITIONS]: companyComposition,
  [PagePaths.COMPANY_SUMMARY]: companyOverview,
  [PagePaths.COMPANY_PAY]: companyPay,
  [PagePaths.COMPANY_POSTINGS]: companyPostings,
  [PagePaths.COMPANY_SENTIMENT]: companySentiment,
  [PagePaths.COMPANY_TRANSITIONS]: companyTransitions,

  [PagePaths.GEOGRAPHY_COMPOSITIONS]: geographyComposition,
  [PagePaths.GEOGRAPHY_SUMMARY]: geographyOverview,
  [PagePaths.GEOGRAPHY_PAY]: geographyPay,
  [PagePaths.GEOGRAPHY_POSTINGS]: geographyPostings,
  [PagePaths.GEOGRAPHY_SENTIMENT]: geographySentiment,
  [PagePaths.GEOGRAPHY_TRANSITIONS]: geographyTransitions,

  [PagePaths.ROLE_COMPOSITIONS]: roleComposition,
  [PagePaths.ROLE_SUMMARY]: roleOverview,
  [PagePaths.ROLE_PAY]: rolePay,
  [PagePaths.ROLE_POSTINGS]: rolePostings,
  [PagePaths.ROLE_SENTIMENT]: roleSentiment,
  [PagePaths.ROLE_TRANSITIONS]: roleTransitions,
} as const satisfies Partial<Record<PagePaths, Page>>;

export type PublicCustomDashPath = keyof typeof PathToPageConfigLookup;

const createReverseLookup = <T extends Record<string, { id: string }>>(
  map: T
) =>
  Object.fromEntries(
    Object.entries(map).map(([key, value]) => [value.id, key as keyof T])
  ) as Record<string, keyof T>;

const PageIdToPathLookup = createReverseLookup(PathToPageConfigLookup);

/** Paths not covered by {@link PathToPageConfigLookup} (e.g. screener slugs). */
const pageIdToExplicitPath: Record<string, string> = {
  [companyScreenerV2.id]: PagePaths.COMPANY_SCREENER,
  [geographyScreenerV2.id]: PagePaths.GEOGRAPHY_SCREENER,
  [roleScreenerV2.id]: PagePaths.ROLE_SCREENER,
  [talentDiscovery.id]: PagePaths.TALENT_DISCOVERY,
  [postingDiscovery.id]: PagePaths.POSTING_DISCOVERY,
  // V2 transition pages reuse the same URL slot as their V1 counterparts.
  [companyTransitionsV2.id]: PagePaths.COMPANY_TRANSITIONS,
  [geographyTransitionsV2.id]: PagePaths.GEOGRAPHY_TRANSITIONS,
  [roleTransitionsV2.id]: PagePaths.ROLE_TRANSITIONS,
  // V2 composition pages reuse the same URL slot as their V1 counterparts.
  [companyCompositionV2.id]: PagePaths.COMPANY_COMPOSITIONS,
  [geographyCompositionV2.id]: PagePaths.GEOGRAPHY_COMPOSITIONS,
  [roleCompositionV2.id]: PagePaths.ROLE_COMPOSITIONS,
};

export const getPathForDashboardId = (id: string): string => {
  if (PageIdToPathLookup[id]) {
    return '/' + PageIdToPathLookup[id];
  }
  const explicit = pageIdToExplicitPath[id];
  if (explicit) {
    return '/' + explicit;
  }

  return '';
};

export const getPageConfigForPath = (path: PublicCustomDashPath): Page =>
  PathToPageConfigLookup[path];
